/** UI clocks use the selected display timezone. APIs stay UTC. */

export const SINGAPORE_TZ = 'Asia/Singapore';
export const DEFAULT_DISPLAY_TIMEZONE = SINGAPORE_TZ;
export const DISPLAY_TIMEZONE_STORAGE_KEY = 'scube-display-timezone';

const SG_LOCALE = 'en-SG';

export const DISPLAY_TIMEZONES: { value: string; label: string }[] = [
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dubai', label: 'Gulf (GST)' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'America/New_York', label: 'New York (ET)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'UTC', label: 'UTC' },
];

const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'Asia/Singapore': 'SGT',
  'Asia/Kolkata': 'IST',
  'Asia/Calcutta': 'IST',
  'Asia/Dubai': 'GST',
  'Asia/Jakarta': 'WIB',
  'Asia/Bangkok': 'ICT',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Tokyo': 'JST',
  UTC: 'UTC',
  'Etc/UTC': 'UTC',
};

export function isValidTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat('en', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function getDisplayTimezone(): string {
  if (typeof localStorage === 'undefined') return DEFAULT_DISPLAY_TIMEZONE;
  try {
    const stored = localStorage.getItem(DISPLAY_TIMEZONE_STORAGE_KEY);
    if (stored && isValidTimeZone(stored)) return stored;
  } catch {
    /* ignore */
  }
  return DEFAULT_DISPLAY_TIMEZONE;
}

export function persistDisplayTimezone(timeZone: string): void {
  if (!isValidTimeZone(timeZone)) return;
  try {
    localStorage.setItem(DISPLAY_TIMEZONE_STORAGE_KEY, timeZone);
  } catch {
    /* ignore */
  }
}

export function parseInstant(value: Date | string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const iso = !hasZone && /^\d{4}-\d{2}-\d{2}T/.test(trimmed) ? `${trimmed}Z` : trimmed;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function timeZoneAbbr(date: Date, timeZone: string): string {
  const mapped = TIMEZONE_ABBREVIATIONS[timeZone];
  if (mapped) return mapped;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
}

export function formatDateTime(
  iso: Date | string | null | undefined,
  timeZone: string = getDisplayTimezone(),
): string {
  const date = parseInstant(iso);
  if (!date) return '—';
  const zone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_DISPLAY_TIMEZONE;
  const formatted = date.toLocaleString(SG_LOCALE, {
    timeZone: zone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${formatted} ${timeZoneAbbr(date, zone)}`;
}

export function formatDate(
  iso: Date | string | null | undefined,
  timeZone: string = getDisplayTimezone(),
): string {
  const date = parseInstant(iso);
  if (!date) return '—';
  const zone = isValidTimeZone(timeZone) ? timeZone : DEFAULT_DISPLAY_TIMEZONE;
  return date.toLocaleDateString(SG_LOCALE, {
    timeZone: zone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
